import React, { useState, useEffect } from 'react';
import { processCertificate } from '../utils/ocr';

function CertificateModal({ isOpen, onClose, onSave, certificate }) {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    issue_date: '',
    expiry_date: '',
    notes: '',
  });
  const [units, setUnits] = useState([]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanMessage, setScanMessage] = useState('');
  const [scanStatus, setScanStatus] = useState(null); // 'success', 'partial', 'failed', null

  // Reset form when modal opens/closes or certificate changes
  useEffect(() => {
    if (isOpen) {
      if (certificate) {
        // Editing existing certificate
        setFormData({
          name: certificate.certificate_name || certificate.name || '',
          type: certificate.type || '',
          issue_date: certificate.issue_date || '',
          expiry_date: certificate.expiry_date || '',
          notes: certificate.notes || '',
        });
        // fileName removed - was unused
        setUnits(certificate.units || []);
      } else {
        // Adding new certificate
        setFormData({
          name: '',
          type: '',
          issue_date: '',
          expiry_date: '',
          notes: '',
        });
        setUnits([]);
        // fileName removed - was unused
      }
      setFile(null);
      setPreviewUrl(null);
      setZoom(100);
      setIsScanning(false);
      setScanProgress(0);
      setScanMessage('');
      setScanStatus(null);
    }
  }, [isOpen, certificate]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file selection (no auto-scan)
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      alert('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    setFile(selectedFile);

    // Create preview URL
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    
    // Reset scan status
    setScanStatus(null);
    setScanMessage('');
  };

  // Handle auto-scan button click
  const handleAutoScan = async () => {
    if (!file) return;

    try {
      setIsScanning(true);
      setScanMessage('Reading certificate...');
      setScanProgress(0);
      setScanStatus(null);

      const result = await processCertificate(file, (progress) => {
        setScanProgress(progress);
      });

      // Auto-fill form with extracted data (only if fields are empty)
      if (result.certificateName && !formData.name) {
        setFormData((prev) => ({ ...prev, name: result.certificateName }));
      }
      if (result.type && !formData.type) {
        setFormData((prev) => ({ ...prev, type: result.type }));
      }
      if (result.issueDate && !formData.issue_date) {
        setFormData((prev) => ({ ...prev, issue_date: result.issueDate }));
      }
      if (result.expiryDate && !formData.expiry_date) {
        setFormData((prev) => ({ ...prev, expiry_date: result.expiryDate }));
      }
      
      // Set units if found
      if (result.units && result.units.length > 0) {
        setUnits(result.units);
      }

      // Determine scan status
      const fieldsFound = [
        result.certificateName,
        result.type,
        result.issueDate,
      ].filter(Boolean).length;

      if (fieldsFound === 0) {
        setScanStatus('failed');
        setScanMessage("Couldn't read the certificate automatically. Please fill in manually.");
      } else if (fieldsFound < 3) {
        setScanStatus('partial');
        setScanMessage(`Found some info. Please check and fill in the rest.`);
      } else {
        setScanStatus('success');
        setScanMessage('Certificate scanned! Please verify the details.');
      }

    } catch (error) {
      console.error('OCR error:', error);
      setScanStatus('failed');
      setScanMessage('Scan failed. Please enter details manually.');
    } finally {
      setIsScanning(false);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setFile(null);
    setPreviewUrl(null);
    setScanStatus(null);
    setScanMessage('');
    setScanProgress(0);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    document.getElementById('fileInput').value = '';
  };

  // Handle zoom controls
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 20, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 20, 60));
  };

  const handleZoomReset = () => {
    setZoom(100);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('certificate_name', formData.name);
    submitData.append('name', formData.name); // Keep for backwards compatibility
    submitData.append('type', formData.type);
    submitData.append('issue_date', formData.issue_date);
    submitData.append('expiry_date', formData.expiry_date || '');
    submitData.append('notes', formData.notes || '');
    
    // Add units if present
    if (units.length > 0) {
      submitData.append('units', JSON.stringify(units));
    }

    if (file) {
      submitData.append('file', file);
    }

    await onSave(submitData, certificate?.id);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {certificate ? 'Edit Certificate' : 'Add Certificate'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body-split">
          {/* LEFT SIDE: Preview */}
          <div className="preview-section">
            {!previewUrl ? (
              <div
                className="file-upload-zone"
                onClick={() => document.getElementById('fileInput').click()}
              >
                <div className="upload-icon">📄</div>
                <div className="upload-text">
                  <strong>Click to upload certificate</strong>
                  <br />
                  <small>PDF, JPG, PNG (Max 10MB)</small>
                </div>
              </div>
            ) : (
              <>
                <div className="preview-controls">
                  <div className="btn-scan-wrapper">
                    <button
                      type="button"
                      className="btn-scan"
                      onClick={handleAutoScan}
                      disabled={isScanning}
                      title="We'll read your file and fill in the details for you"
                    >
                      {isScanning ? '⏳ Reading certificate...' : '🔍 Scan & Fill My Certificate'}
                    </button>
                    {!isScanning && (
                      <small className="btn-scan-hint">
                        We'll read your file and fill in the details
                      </small>
                    )}
                  </div>
                  <div className="zoom-controls">
                    <button type="button" onClick={handleZoomOut} title="Zoom out">
                      &#8722;
                    </button>
                    <button type="button" onClick={handleZoomReset} title="Reset zoom">
                      {zoom}%
                    </button>
                    <button type="button" onClick={handleZoomIn} title="Zoom in">
                      &#43;
                    </button>
                  </div>
                </div>

                {isScanning && (
                  <div className="scan-progress">
                    <div className="scan-progress-bar">
                      <div
                        className="scan-progress-fill"
                        style={{ width: `${scanProgress}%` }}
                      />
                    </div>
                    <small>{scanMessage} ({scanProgress}%)</small>
                  </div>
                )}

                {scanMessage && !isScanning && (
                  <div className={`scan-result scan-result-${scanStatus}`}>
                    <small>{scanMessage}</small>
                  </div>
                )}

                <div className="preview-container">
                  {file?.type === 'application/pdf' ? (
                    <iframe
                      src={previewUrl}
                      title="Certificate preview"
                      className="preview-frame"
                      style={{ transform: `scale(${zoom / 100})` }}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Certificate preview"
                      className="preview-image"
                      style={{ transform: `scale(${zoom / 100})` }}
                    />
                  )}
                </div>

                <button
                  type="button"
                  className="btn-change-file"
                  onClick={handleRemoveFile}
                >
                  Change File
                </button>
              </>
            )}

            <input
              type="file"
              id="fileInput"
              className="file-input"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* RIGHT SIDE: Form */}
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Certificate Name *</label>
                <input
                  type="text"
                  className="form-input"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Certificate IV in Occupational Health and Safety BSB41407"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Certificate Type *</label>
                <select
                  className="form-select"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select the category this certificate belongs to...</option>
                  <option value="First Aid">First Aid</option>
                  <option value="Safety">Safety</option>
                  <option value="License">License</option>
                  <option value="Ticket">Ticket</option>
                  <option value="Training">Training</option>
                  <option value="Medical">Medical</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Certificate IV">Certificate IV</option>
                  <option value="Certificate III">Certificate III</option>
                  <option value="Certificate II">Certificate II</option>
                  <option value="General Certificate">General Certificate</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Issue Date *</label>
                <input
                  type="date"
                  className="form-input"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Expiry Date</label>
                <input
                  type="date"
                  className="form-input"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleChange}
                />
                <small className="form-helper">Leave blank if this certificate doesn't expire</small>
              </div>

              {/* Units Section */}
              {units.length > 0 && (
                <div className="form-group form-group-units">
                  <label className="form-label">
                    Units of Competency ({units.length})
                  </label>
                  <div className="units-preview">
                    {units.map((unit, index) => (
                      <div key={index} className="unit-row">
                        <span className="unit-code">{unit.code}</span>
                        <span className="unit-name">{unit.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isScanning}>
                  {certificate ? 'Update Certificate' : 'Save Certificate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CertificateModal;
